import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent, type MouseEvent } from "react";
import { createPortal } from "react-dom";

import { Popover } from "./stoffe/Popover";

export type BildOption = { wert: string; label: string; bildUrl?: string | null; zusatz?: string | null; gesperrt?: boolean };

type Props = {
  id?: string;
  optionen: BildOption[];
  /** "" = nichts gewählt. */
  wert: string;
  onChange(wert: string): void;
  /** Knopftext ohne Auswahl. */
  platzhalter: string;
  /** Wählbare Leer-Zeile mit dieser Beschriftung; null/undefined = keine. */
  leerOption?: string | null;
  disabled?: boolean;
  /** Feldname für Screenreader: Der Knopf heißt dann „Name: Wert“ (vor einem `<label htmlFor>`), die Liste „Name“. */
  label?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
};

/** So lange gelten getippte Zeichen als ein Suchbegriff. */
const TIPP_PAUSE_MS = 700;

/** So viel größer zeigt die Lupe ein Vorschaubild, über dem die Maus steht. */
const LUPE_FAKTOR = 3;
/** Mindestabstand der Lupe zum Fensterrand. */
const LUPE_RAND = 8;

/** Vergrößertes Vorschaubild: Adresse und Lage am Bildschirm. */
type Lupe = { url: string; stil: CSSProperties };

/**
 * Die Lupe wächst aus dem Vorschaubild heraus: linke Kante bündig, senkrecht mittig, im Fenster
 * gehalten. Der Ursprung der Einblendung liegt in der Mitte des kleinen Bilds.
 */
function lupeUeber(url: string, r: DOMRect): Lupe {
  const breite = Math.round(r.width * LUPE_FAKTOR);
  const hoehe = Math.round(r.height * LUPE_FAKTOR);
  const left = Math.max(LUPE_RAND, Math.min(r.left, window.innerWidth - breite - LUPE_RAND));
  const top = Math.max(LUPE_RAND, Math.min(r.top + r.height / 2 - hoehe / 2, window.innerHeight - hoehe - LUPE_RAND));
  const ursprung = `${r.left + r.width / 2 - left}px ${r.top + r.height / 2 - top}px`;
  return { url, stil: { left, top, width: breite, height: hoehe, transformOrigin: ursprung } };
}

type BildProps = {
  url?: string | null;
  groesse: 28 | 40;
  kaputt: ReadonlySet<string>;
  onFehler(url: string): void;
  /** Maus auf dem Bild (Lupe zeigen) bzw. wieder herunter (`null`). */
  onLupe(lupe: Lupe | null): void;
};

/** Vorschaubild; ohne Adresse oder nach einem Ladefehler ein gestreifter Platzhalter gleicher Größe. */
function Bild({ url, groesse, kaputt, onFehler, onLupe }: BildProps) {
  const klasse = groesse === 28 ? "bild-auswahl__bild bild-auswahl__bild--klein" : "bild-auswahl__bild";
  if (!url || kaputt.has(url)) return <span className={`${klasse} bild-auswahl__bild--fehlt`} aria-hidden />;
  return (
    <img
      className={klasse}
      src={url}
      width={groesse}
      height={groesse}
      alt=""
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => onFehler(url)}
      onMouseEnter={(e) => onLupe(lupeUeber(url, e.currentTarget.getBoundingClientRect()))}
      onMouseLeave={() => onLupe(null)}
    />
  );
}

function Text({ option }: { option: BildOption }) {
  return (
    <span className="bild-auswahl__text">
      <span className="bild-auswahl__label">{option.label}</span>
      {option.zusatz && <span className="bild-auswahl__zusatz">{option.zusatz}</span>}
    </span>
  );
}

/**
 * Einfachauswahl wie ein `<select>`, aber mit Vorschaubild je Zeile (z. B. Stofffarben).
 * Knopf mit Popover; die Liste ist eine ARIA-Listbox, die beim Öffnen den Fokus bekommt.
 */
export function BildAuswahl({ id, optionen, wert, onChange, platzhalter, leerOption, disabled, label, ...aria }: Props) {
  const eigeneId = useId();
  const knopfId = id ?? `${eigeneId}-knopf`;
  const listeId = `${eigeneId}-liste`;
  const optionId = (i: number) => `${eigeneId}-option-${i}`;

  const knopf = useRef<HTMLButtonElement>(null);
  const liste = useRef<HTMLUListElement>(null);
  const [offen, setOffen] = useState(false);
  const [aktiv, setAktiv] = useState(-1);
  // Breite des Knopfs beim Öffnen – die Liste wird mindestens so breit.
  const [breite, setBreite] = useState<number | null>(null);
  // Bildadressen, die nicht geladen haben: danach nur noch der Platzhalter, kein neuer Versuch.
  const [kaputt, setKaputt] = useState<ReadonlySet<string>>(() => new Set());
  // Vergrößertes Vorschaubild, solange die Maus auf einem Bild steht.
  const [lupe, setLupe] = useState<Lupe | null>(null);
  const suche = useRef({ text: "", zeit: 0 });
  // Nur echte Mausbewegung setzt die aktive Zeile – nicht die Liste, die unter der ruhenden Maus scrollt.
  const maus = useRef<{ x: number; y: number } | null>(null);

  const zeilen: BildOption[] = leerOption != null ? [{ wert: "", label: leerOption }, ...optionen] : optionen;
  // Ein Wert, der (noch) nicht in den Optionen steht, bleibt sichtbar statt still zu verschwinden.
  const gewaehlt = wert ? (optionen.find((o) => o.wert === wert) ?? { wert, label: wert }) : null;

  const bildFehler = (url: string) => setKaputt((alt) => (alt.has(url) ? alt : new Set(alt).add(url)));

  /** Erste wählbare Zeile ab `start` in Richtung `schritt`; -1, wenn keine. */
  function naechste(start: number, schritt: 1 | -1) {
    for (let i = start; i >= 0 && i < zeilen.length; i += schritt) if (!zeilen[i].gesperrt) return i;
    return -1;
  }

  function aktivieren(i: number) {
    if (i < 0) return;
    setAktiv(i);
    liste.current?.children[i]?.scrollIntoView({ block: "nearest" });
  }

  function umschalten(neu: boolean) {
    if (!neu) {
      setOffen(false);
      return;
    }
    if (disabled) return;
    if (offen) {
      liste.current?.focus();
      return;
    }
    const i = zeilen.findIndex((o) => o.wert === wert);
    setAktiv(i >= 0 && !zeilen[i].gesperrt ? i : naechste(0, 1));
    setBreite(knopf.current?.offsetWidth ?? null);
    setOffen(true);
  }

  function schliessen() {
    // Erst der Knopf, dann zu: So bleibt der Fokus im Popover und fällt nicht ins Leere.
    knopf.current?.focus();
    setOffen(false);
  }

  function waehlen(i: number) {
    const o = zeilen[i];
    if (!o || o.gesperrt) return;
    if (o.wert !== wert) onChange(o.wert);
    schliessen();
  }

  useEffect(() => {
    if (!offen) return;
    // Das Popover fokussiert nur Eingaben und Knöpfe – die Liste nimmt den Fokus selbst.
    liste.current?.focus({ preventScroll: true });
    if (aktiv >= 0) liste.current?.children[aktiv]?.scrollIntoView({ block: "nearest" });
    // Nur beim Öffnen; danach hält die Tastatursteuerung die Zeile selbst sichtbar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);

  useEffect(() => {
    if (disabled) setOffen(false);
  }, [disabled]);

  // Öffnen oder Schließen nimmt die Lupe weg – beim Wählen verschwindet das Bild unter der Maus,
  // ohne dass ein mouseleave kommt.
  useEffect(() => {
    setLupe(null);
  }, [offen]);

  // Scrollt die Liste oder die Seite, stünde die Lupe neben dem Bild: dann weg damit.
  useEffect(() => {
    if (!lupe) return;
    const weg = () => setLupe(null);
    window.addEventListener("scroll", weg, true);
    window.addEventListener("resize", weg);
    return () => {
      window.removeEventListener("scroll", weg, true);
      window.removeEventListener("resize", weg);
    };
  }, [lupe]);

  function knopfTaste(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key !== "ArrowDown" && e.key !== "Enter" && e.key !== " ") return;
    // Selbst öffnen statt über den Klick des Browsers – den löst die Leertaste erst beim Loslassen aus.
    e.preventDefault();
    umschalten(true);
  }

  function listeTaste(e: KeyboardEvent<HTMLUListElement>) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        aktivieren(naechste(aktiv + 1, 1));
        return;
      case "ArrowUp":
        e.preventDefault();
        aktivieren(naechste(aktiv - 1, -1));
        return;
      case "Home":
        e.preventDefault();
        aktivieren(naechste(0, 1));
        return;
      case "End":
        e.preventDefault();
        aktivieren(naechste(zeilen.length - 1, -1));
        return;
      case "Enter":
      case " ":
        e.preventDefault();
        waehlen(aktiv);
        return;
      case "Tab":
        // Zurück zum Knopf statt weiter: Mit Umschalt+Tab bliebe der Fokus sonst im Popover und die Liste offen.
        e.preventDefault();
        schliessen();
        return;
    }
    // Tippsuche: Zeichen sammeln sich bis zur Pause; die erste passende Zeile wird aktiv.
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
    const jetzt = Date.now();
    const text = (jetzt - suche.current.zeit > TIPP_PAUSE_MS ? "" : suche.current.text) + e.key.toLowerCase();
    suche.current = { text, zeit: jetzt };
    aktivieren(zeilen.findIndex((o) => !o.gesperrt && o.label.toLowerCase().startsWith(text)));
  }

  function mausBewegt(e: MouseEvent, i: number) {
    const letzte = maus.current;
    maus.current = { x: e.screenX, y: e.screenY };
    if (letzte && letzte.x === e.screenX && letzte.y === e.screenY) return;
    if (i !== aktiv && !zeilen[i].gesperrt) setAktiv(i);
  }

  const knopfText = gewaehlt ? `${gewaehlt.label}${gewaehlt.zusatz ? ` (${gewaehlt.zusatz})` : ""}` : platzhalter;

  return (
    <span className="bild-auswahl">
      <Popover
        offen={offen}
        onOffen={umschalten}
        label={label ?? platzhalter}
        className="bild-auswahl__popover"
        ausloeser={
          <button
            id={knopfId}
            ref={knopf}
            type="button"
            className={gewaehlt ? "bild-auswahl__knopf" : "bild-auswahl__knopf bild-auswahl__knopf--leer"}
            aria-haspopup="listbox"
            aria-expanded={offen}
            aria-controls={offen ? listeId : undefined}
            aria-label={label ? `${label}: ${knopfText}` : undefined}
            disabled={disabled}
            // Safari fokussiert Knöpfe beim Klick nicht; der Fokus soll nach dem Schließen hier stehen.
            onClick={(e) => e.currentTarget.focus()}
            onKeyDown={knopfTaste}
            // Firefox klickt sonst beim Loslassen der Leertaste, mit der eben in der Liste gewählt wurde.
            onKeyUp={(e) => {
              if (e.key === " ") e.preventDefault();
            }}
            {...aria}
          >
            {gewaehlt ? (
              <>
                <Bild url={gewaehlt.bildUrl} groesse={28} kaputt={kaputt} onFehler={bildFehler} onLupe={setLupe} />
                <Text option={gewaehlt} />
              </>
            ) : (
              <span className="bild-auswahl__text">
                <span className="bild-auswahl__label">{platzhalter}</span>
              </span>
            )}
          </button>
        }
      >
        <ul
          ref={liste}
          id={listeId}
          role="listbox"
          tabIndex={-1}
          aria-label={label}
          aria-labelledby={label ? undefined : knopfId}
          aria-activedescendant={aktiv >= 0 && aktiv < zeilen.length ? optionId(aktiv) : undefined}
          className="bild-auswahl__liste"
          style={breite ? { minWidth: breite - 2 } : undefined}
          onKeyDown={listeTaste}
        >
          {zeilen.map((o, i) => (
            <li
              key={`${i}:${o.wert}`}
              id={optionId(i)}
              role="option"
              aria-selected={o.wert === wert}
              aria-disabled={o.gesperrt || undefined}
              className={i === aktiv ? "bild-auswahl__option bild-auswahl__option--aktiv" : "bild-auswahl__option"}
              onMouseMove={(e) => mausBewegt(e, i)}
              onClick={() => waehlen(i)}
            >
              {leerOption != null && i === 0 ? (
                <span className="bild-auswahl__bild bild-auswahl__bild--leer" aria-hidden />
              ) : (
                <Bild url={o.bildUrl} groesse={40} kaputt={kaputt} onFehler={bildFehler} onLupe={setLupe} />
              )}
              <Text option={o} />
            </li>
          ))}
        </ul>
      </Popover>
      {/* Per Portal an body: Die Liste scrollt (overflow) und schnitte ein vergrößertes Bild sonst ab. */}
      {lupe &&
        createPortal(
          <img
            className="bild-auswahl__lupe"
            src={lupe.url}
            alt=""
            aria-hidden
            referrerPolicy="no-referrer"
            style={lupe.stil}
            onError={() => setLupe(null)}
          />,
          document.body,
        )}
    </span>
  );
}
